import React, { Component, PropTypes } from "react";
import { connect } from "react-redux";
import { FormattedMessage, FormattedDate } from "react-intl";
import { bindActionCreators } from "redux";
import classNames from "classnames";
import { ButtonToolbar } from "react-bootstrap";

import { DataTable } from "../../../../../../common/DataTable";
import {
  CheckboxButton,
  EditButton,
  ArchiveButton,
  Render,
} from "../../../../../../common";
import BasicBeneficiaryFields from "./BasicBeneficiaryFields";

import { BENEFICIARIES, MEMBERS } from "../../../../../../../utils/data/models";
import * as dataActions from "../../../../../../../utils/data/dataActions";
import { ADMINISTRATOR, AGENT, HR_CLERK, DIRECTOR, FINANCE, FINTECH, INFORMATION_AGENT, MANAGER, PLAN_ADMINISTRATOR } from "../../../../../../../utils/Roles";
import userHasAccess from "../../../../../../../utils/userHasAccess";
import { ACTIVE, IMPLEMENTATION, INACTIVE, TERMINATED } from "../../../../enums/clientStatusTypes";
import { toLocaleDate } from "../../../../../../common/dateHelper";

class BeneficiariesDataTable extends Component {
  constructor(props, context) {
    super(props, context);

    this.createEmptyBeneficiaryModel = this.createEmptyBeneficiaryModel.bind(
      this
    );
    this.getData = this.getData.bind(this);
    this.createBeneficiary = this.createBeneficiary.bind(this);
    this.generateAddButton = this.generateAddButton.bind(this);
    this.getCols = this.getCols.bind(this);
    this.setCellClassNames = this.setCellClassNames.bind(this);
    this.generateActionButtons = this.generateActionButtons.bind(this);
    this.updateBeneficiary = this.updateBeneficiary.bind(this);
    this.archiveBeneficiary = this.archiveBeneficiary.bind(this);
    this.toggleArchivedBeneficiaries = this.toggleArchivedBeneficiaries.bind(
      this
    );
    this.getViewOnlyForClientStatusType = this.getViewOnlyForClientStatusType.bind(this);

    this.viewBeneficiaryForImplementation = !userHasAccess(FINANCE);
    this.viewBeneficiaryForActive = !userHasAccess([DIRECTOR, MANAGER, AGENT, PLAN_ADMINISTRATOR, ADMINISTRATOR, FINTECH, HR_CLERK], true);
    this.viewBeneficiaryForInactive = !userHasAccess([ADMINISTRATOR, FINTECH, DIRECTOR, MANAGER, AGENT], true);
    this.viewBeneficiaryForTerminated = !userHasAccess(FINTECH);
  }

  componentWillUnmount() {
    this.props.dataActions.clearDataList(BENEFICIARIES);
  }

  getViewOnlyForClientStatusType() {
      const { clientStatusType, activeMember } = this.props;

      const isMemberArchived = activeMember && activeMember.isArchived && !userHasAccess(FINTECH);

      switch(clientStatusType) {
        case IMPLEMENTATION.value:
          return this.viewBeneficiaryForImplementation || isMemberArchived;
        case ACTIVE.value:
          return this.viewBeneficiaryForActive || isMemberArchived;
        case INACTIVE.value:
          return this.viewBeneficiaryForInactive || isMemberArchived;
        case TERMINATED.value:
          return this.viewBeneficiaryForTerminated ||isMemberArchived;
      }
  }

  getData(offset, limit, q, sortBy, filters) {
    return this.props.dataActions.getDataList(
      BENEFICIARIES,
      offset,
      limit,
      q,
      sortBy,
      filters,
      null,
      { memberId: this.props.memberId }
    );
  }

  createEmptyBeneficiaryModel() {
    return {
      memberId: this.props.memberId,
      firstName: "",
      lastName: "",
      percentageSplit: "",
      birthdate: "",
      relationship: "",
      isRevocable: false,
      isMinor: false,

      fiduciaryLastName: null,
      fiduciaryFirstName: null,
      fiduciaryBirthdate: null,
      fiduciaryRelationship: null,
    };
  }

  createBeneficiary(values) {
    this.props.dataActions.createDataItem(BENEFICIARIES, values).then(() => {
      this.getData(
        this.props.offset,
        this.props.limit,
        this.props.q,
        this.props.sortBy,
        this.props.filters
      );
      this.props.dataActions.getDataItem(MEMBERS, this.props.memberId, {
        fields:
          "memberCoverages(memberCoverageGuarantees(*),*),dependentPersons(*),beneficiaries(isArchived),*",
      });
    });
  }

  setCellClassNames(beneficiary) {
    return classNames({
      archived: beneficiary.isArchived,
    });
  }

  toggleArchivedBeneficiaries() {
    const isArchivedFiltered = this.props.filters.includes(
      BENEFICIARIES.archiveFilter
    );
    const filters = isArchivedFiltered
      ? this.props.filters.filter(
          (filter) => filter != BENEFICIARIES.archiveFilter
        )
      : [...this.props.filters, BENEFICIARIES.archiveFilter];
    this.getData(
      this.props.offset,
      this.props.limit,
      this.props.q,
      this.props.sortBy,
      filters
    );
  }

  generateAddButton(canAddBeneficiary) {
    const viewOnly = userHasAccess([INFORMATION_AGENT], true) || !canAddBeneficiary;
    return (
      <div>
        <CheckboxButton
          className="include-archived"
          id="archived"
          label={
            <FormattedMessage id="beneficiaries.datatable.actions.includeArchivedBeneficiaries" />
          }
          checked={!this.props.filters.includes(BENEFICIARIES.archiveFilter)}
          onClick={this.toggleArchivedBeneficiaries}
        />
        {!viewOnly && (
          <EditButton
            allowSubmission={!viewOnly}
            title={<FormattedMessage id="beneficiaries.form.title" />}
            label={<FormattedMessage id="common.add" />}
            add={true}
            form={BasicBeneficiaryFields}
            data={this.createEmptyBeneficiaryModel()}
            editFunction={this.createBeneficiary}
          />
        )}
      </div>
    );
  }

  getCols() {
    return [
      {
        name: <FormattedMessage id="beneficiaries.datatable.columns.name" />,
        sortKeys: ["firstName", "lastName"],
        display: (beneficiary) => (
          <span
            className={this.setCellClassNames(beneficiary)}
          >{`${beneficiary.firstName} ${beneficiary.lastName}`}</span>
        ),
        widthPct: 16,
      },
      {
        name: (
          <FormattedMessage id="beneficiaries.datatable.columns.relationship" />
        ),
        key: "relationship",
        display: (beneficiary) => (
          <span
            className={this.setCellClassNames(beneficiary)}
          >{`${beneficiary.relationship}`}</span>
        ),
        widthPct: 10,
      },
      {
        name: (
          <FormattedMessage id="beneficiaries.datatable.columns.birthdate" />
        ),
        key: "birthdate",
        display: (beneficiary) => (
          <span className={this.setCellClassNames(beneficiary)}>
            <FormattedDate
              value={toLocaleDate(beneficiary.birthdate)}
              year="numeric"
              month="long"
              day="2-digit"
              timeZone="UTC"
            />
          </span>
        ),
        widthPct: 19,
      },
      {
        name: (
          <FormattedMessage id="beneficiaries.datatable.columns.percentageSplit" />
        ),
        key: "percentageSplit",
        display: (beneficiary) => (
          <span
            className={this.setCellClassNames(beneficiary)}
          >{`${beneficiary.percentageSplit} %`}</span>
        ),
        widthPct: 18,
      },
      {
        name: (
          <FormattedMessage id="beneficiaries.datatable.columns.isRevocable" />
        ),
        key: "isRevocable",
        display: (beneficiary) => (
          <span className={this.setCellClassNames(beneficiary)}>
            {beneficiary.isRevocable ? (
              <FormattedMessage id="common.yes" />
            ) : (
              <FormattedMessage id="common.no" />
            )}
          </span>
        ),
        widthPct: 10,
      },
      {
        name: <FormattedMessage id="beneficiaries.datatable.columns.isMinor" />,
        key: "isMinor",
        display: (beneficiary) => (
          <span className={this.setCellClassNames(beneficiary)}>
            {!!beneficiary.fiduciaryFirstName ||
            !!beneficiary.fiduciaryLastName ? (
              <FormattedMessage id="common.yes" />
            ) : (
              <FormattedMessage id="common.no" />
            )}
          </span>
        ),
        widthPct: 10,
      },
      {
        name: <FormattedMessage id="datatable.actions" />,
        display: (beneficiary) => this.generateActionButtons(beneficiary),
        widthPct: 17,
      },
    ];
  }

  updateBeneficiary(values) {
    this.props.dataActions.updateDataItem(BENEFICIARIES, values).then(() => {
      this.getData(
        this.props.offset,
        this.props.limit,
        this.props.q,
        this.props.sortBy,
        this.props.filters
      );
    });
  }

  archiveBeneficiary(beneficiary) {
    beneficiary.isArchived = !beneficiary.isArchived;
    beneficiary.isMinor =
      !!beneficiary.fiduciaryFirstName || !!beneficiary.fiduciaryLastName;
    this.props.dataActions
      .updateDataItem(BENEFICIARIES, beneficiary)
      .then(() => {
        this.getData(
          this.props.offset,
          this.props.limit,
          this.props.q,
          this.props.sortBy,
          this.props.filters
        );
        this.props.dataActions.getDataItem(MEMBERS, this.props.memberId, {
          fields:
            "memberCoverages(memberCoverageGuarantees(*),*),dependentPersons(*),beneficiaries(isArchived),*",
        });
      });
  }

  generateActionButtons(beneficiary) {
    const viewOnly = this.getViewOnlyForClientStatusType();

    return (
      <ButtonToolbar key={beneficiary.beneficiaryId}>
        <EditButton
          title={
            <FormattedMessage
              id={`beneficiaries.datatable.actions.${
                viewOnly ? "view" : "edit"
              }`}
            />
          }
          label={
            <FormattedMessage id={viewOnly ? "common.view" : "common.edit"} />
          }
          form={BasicBeneficiaryFields}
          props={{ disabled: viewOnly }}
          allowSubmission={!viewOnly}
          data={{
            ...beneficiary,
            isRevocable: beneficiary.isRevocable,
            isMinor:
              !!beneficiary.fiduciaryFirstName ||
              !!beneficiary.fiduciaryLastName
                ? true
                : false,
          }}
          editFunction={this.updateBeneficiary}
        />
        <Render for={[AGENT, HR_CLERK]} if={!beneficiary.isArchived}>
          <ArchiveButton
            isArchived={beneficiary.isArchived}
            archivedName={`${beneficiary.firstName} ${beneficiary.lastName}`}
            archiveFunction={this.archiveBeneficiary.bind(null, beneficiary)}
            disabled={viewOnly}
            label={
              beneficiary.isArchived ? (
                <FormattedMessage id="common.unarchive" />
              ) : (
                <FormattedMessage id="common.archive" />
              )
            }
          />
        </Render>
      </ButtonToolbar>
    );
  }

  render() {
    const canAddBeneficiary = !this.getViewOnlyForClientStatusType();
    return (
      <div>
        <DataTable
          ref="dataTable"
          title={<FormattedMessage id="beneficiaries.title" />}
          getData={this.getData}
          cols={this.getCols()}
          q={this.props.q}
          filters={this.props.filters}
          offset={this.props.offset}
          data={this.props.items}
          rowsPerPage={this.props.limit}
          totalRows={this.props.total}
          sortBy={this.props.sortBy}
          addButton={this.generateAddButton(canAddBeneficiary)}
        />
      </div>
    );
  }
}

function mapStateToProps(state) {
  const data = state.data.beneficiaries || {};
  const pagination = data.pagination || {};
  const activeMember = state.data.members.active;
  const memberId = activeMember.memberId;
  const activeClient = state.data.clients && state.data.clients.active || {};

  return {
    activeMember,
    memberId,
    active: data.active || {},
    authLanguageCode: state.auth.user.languageCode,
    limit: pagination.limit || 100,
    offset: pagination.offset || 0,
    sortBy: data.sortBy || ["firstName:asc", "lastName:asc"],
    q: data.q,
    filters: data.filters || [
      `beneficiaries.memberId==${memberId}`,
      BENEFICIARIES.archiveFilter,
    ],
    total: pagination.totalCount || 0,
    items: data.items || null,
    clientStatusType: activeClient.clientStatusType
  };
}

BeneficiariesDataTable.propTypes = {
  dataActions: PropTypes.object,
  beneficiaryId: PropTypes.number,
  memberId: PropTypes.number,
  limit: PropTypes.number,
  offset: PropTypes.number,
  sortBy: PropTypes.array,
  total: PropTypes.number,
  q: PropTypes.string,
  filters: PropTypes.array,
  items: PropTypes.array,
  clientStatusType: PropTypes.string,
  activeMember: PropTypes.object
};

function mapDispatchToProps(dispatch) {
  return {
    dataActions: bindActionCreators(dataActions, dispatch),
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(BeneficiariesDataTable);



// WEBPACK FOOTER //
// ./src/components/dashboard/clients/client/members/member/beneficiaries/BeneficiariesDataTable.js